import { Button } from '@raas/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@raas/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@raas/components/ui/dropdown-menu';
import { Input } from '@raas/components/ui/input';
import { Label } from '@raas/components/ui/label';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckIcon,
  ChevronsUpDownIcon,
  SettingsIcon,
  UsersIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { LogoUpload } from './components/LogoUpload';
import {
  activateFirstOrganization,
  getActiveMemberRole,
  getActiveOrganization,
  listOrganizations,
  type OrganizationDetails,
  type OrganizationSummary,
  organizationQueryKeys,
  setActiveOrganization,
  type UseOrganizationStateOptions,
  updateOrganizationSettings,
} from './organization-api';

// Re-export API functions for barrel usage
export {
  type ActiveMemberRole,
  activateFirstOrganization,
  getActiveMemberRole,
  getActiveOrganization,
  listOrganizations,
  type OrganizationDetails,
  type OrganizationSummary,
  organizationQueryKeys,
  type SessionLike,
  setActiveOrganization,
  type UseOrganizationStateOptions,
  updateOrganizationSettings,
} from './organization-api';

export function useOrganizationState({
  autoActivate = true,
  refetchSession,
  session,
}: UseOrganizationStateOptions) {
  const queryClient = useQueryClient();
  const userId = typeof session?.user?.id === 'string' ? session.user.id : null;
  const orgId =
    typeof session?.session?.activeOrganizationId === 'string'
      ? session.session.activeOrganizationId
      : null;
  const [isActivatingOrg, setIsActivatingOrg] = useState(false);
  const [switchingOrganizationId, setSwitchingOrganizationId] = useState<
    string | null
  >(null);

  const organizationsQuery = useQuery({
    enabled: !!session,
    queryFn: listOrganizations,
    queryKey: organizationQueryKeys.organizations(userId),
    staleTime: 60_000,
  });

  const activeOrganizationQuery = useQuery({
    enabled: !!session && !!orgId,
    queryFn: getActiveOrganization,
    queryKey: organizationQueryKeys.activeOrganization(orgId, userId),
    staleTime: 15_000,
  });

  const activeMemberRoleQuery = useQuery({
    enabled: !!session && !!orgId,
    queryFn: getActiveMemberRole,
    queryKey: organizationQueryKeys.activeMemberRole(orgId, userId),
    staleTime: 10_000,
  });

  useEffect(() => {
    if (
      !autoActivate ||
      !session ||
      orgId ||
      isActivatingOrg ||
      !organizationsQuery.data?.length
    ) {
      return;
    }

    let cancelled = false;
    setIsActivatingOrg(true);

    void activateFirstOrganization()
      .then(async () => {
        if (!cancelled) {
          await refetchSession();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsActivatingOrg(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    autoActivate,
    isActivatingOrg,
    orgId,
    organizationsQuery.data,
    refetchSession,
    session,
  ]);

  async function switchOrganization(organizationId: string) {
    if (!organizationId || organizationId === orgId) {
      return;
    }

    setSwitchingOrganizationId(organizationId);
    try {
      await setActiveOrganization(organizationId);
      await refetchSession();
      await Promise.all([
        organizationsQuery.refetch(),
        queryClient.invalidateQueries({
          queryKey: ['auth', 'active-member-role'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['auth', 'active-organization'],
        }),
      ]);
    } finally {
      setSwitchingOrganizationId(null);
    }
  }

  async function refreshOrganizationState() {
    await Promise.all([
      organizationsQuery.refetch(),
      activeOrganizationQuery.refetch(),
      activeMemberRoleQuery.refetch(),
    ]);
  }

  return {
    activeOrganization:
      activeOrganizationQuery.data ??
      organizationsQuery.data?.find(
        (organization) => organization.id === orgId,
      ) ??
      null,
    isLoading:
      isActivatingOrg ||
      (!!session && organizationsQuery.isPending) ||
      (!!session && !!orgId && activeOrganizationQuery.isPending) ||
      (!!session && !!orgId && activeMemberRoleQuery.isPending),
    isSwitchingOrganization: switchingOrganizationId !== null,
    memberRole: activeMemberRoleQuery.data?.role ?? null,
    orgId,
    organizations: organizationsQuery.data ?? [],
    refreshOrganizationState,
    switchOrganization,
  };
}

export interface OrganizationSwitcherProps {
  activeOrganizationId: string | null;
  className?: string;
  disabled?: boolean;
  onChange: (organizationId: string) => void | Promise<void>;
  onNavigate?: (path: string) => void;
  organizations: OrganizationSummary[];
}

export function OrganizationSwitcher({
  activeOrganizationId,
  className,
  disabled = false,
  onChange,
  onNavigate: _onNavigate,
  organizations,
}: OrganizationSwitcherProps) {
  const _activeOrg = organizations.find((o) => o.id === activeOrganizationId);

  return (
    <div data-slot="organization-switcher" className={className}>
      <label className="flex min-w-0 items-center gap-2 text-sm">
        <span className="hidden shrink-0 text-xs font-medium tracking-[0.12em] text-muted-foreground uppercase sm:inline">
          当前组织
        </span>
        <select
          aria-label="切换组织"
          className="h-10 min-w-[9.5rem] max-w-[14rem] flex-1 rounded-md border border-border/70 bg-background px-3 text-sm shadow-xs outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[12rem]"
          disabled={disabled || organizations.length === 0}
          value={activeOrganizationId ?? ''}
          onChange={(event) => {
            void onChange(event.target.value);
          }}
        >
          {organizations.length === 0 ? (
            <option value="">暂无可用组织</option>
          ) : null}
          {organizations.map((organization) => (
            <option key={organization.id} value={organization.id}>
              {organization.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sidebar Org Switcher — rich dropdown for sidebar placement         */
/* ------------------------------------------------------------------ */

export interface SidebarOrgSwitcherProps {
  activeOrganizationId: string | null;
  disabled?: boolean;
  onChange: (organizationId: string) => void | Promise<void>;
  onNavigate?: (path: string) => void;
  organizations: OrganizationSummary[];
}

export function SidebarOrgSwitcher({
  activeOrganizationId,
  disabled = false,
  onChange,
  onNavigate,
  organizations,
}: SidebarOrgSwitcherProps) {
  const activeOrg = organizations.find((o) => o.id === activeOrganizationId);

  return (
    <div data-slot="sidebar-org-switcher" className="px-2 pb-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled || organizations.length === 0}
            className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-xs font-semibold text-sidebar-accent-foreground">
              {activeOrg?.name?.charAt(0)?.toUpperCase() ?? '?'}
            </span>
            <span className="min-w-0 flex-1 truncate font-medium text-sidebar-foreground">
              {activeOrg?.name ?? '选择组织'}
            </span>
            <ChevronsUpDownIcon className="size-4 shrink-0 text-sidebar-foreground/50" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className="min-w-[240px]"
          side="bottom"
          align="start"
          sideOffset={6}
        >
          <DropdownMenuLabel>切换组织</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {organizations.map((org) => (
              <DropdownMenuItem
                key={org.id}
                className="group/org-item gap-2.5 py-2"
                onClick={() => void onChange(org.id)}
              >
                <span className="flex size-6 shrink-0 items-center justify-center rounded bg-muted text-xs font-semibold">
                  {org.name?.charAt(0)?.toUpperCase() ?? '?'}
                </span>
                <span className="min-w-0 flex-1 truncate">{org.name}</span>
                {org.id === activeOrganizationId && (
                  <CheckIcon className="size-4 shrink-0 text-primary" />
                )}
                {onNavigate && (
                  <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/org-item:opacity-100">
                    <button
                      type="button"
                      title="管理成员"
                      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigate('/organization/members');
                      }}
                    >
                      <UsersIcon className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      title="组织设置"
                      className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigate('/organization/settings');
                      }}
                    >
                      <SettingsIcon className="size-3.5" />
                    </button>
                  </span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export interface OrganizationSettingsProps {
  onUpdated?: (organization: OrganizationDetails) => void | Promise<void>;
  organization: OrganizationDetails | null;
  showHeader?: boolean;
}

export function OrganizationSettings({
  onUpdated,
  organization,
  showHeader = true,
}: OrganizationSettingsProps) {
  const [name, setName] = useState(organization?.name ?? '');
  const [logo, setLogo] = useState(organization?.logo ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setName(organization?.name ?? '');
    setLogo(organization?.logo ?? '');
    setError(null);
    setSuccess(null);
  }, [organization]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!organization) {
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      const updated = await updateOrganizationSettings({
        logo,
        name,
        organizationId: organization.id,
      });
      setSuccess('组织信息已更新');
      await onUpdated?.(updated);
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : '组织信息更新失败',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      {showHeader ? (
        <CardHeader>
          <CardTitle>基础信息</CardTitle>
          <CardDescription>
            维护当前活跃组织的名称、标识和展示信息。
          </CardDescription>
        </CardHeader>
      ) : null}
      <CardContent className={showHeader ? undefined : 'pt-6'}>
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="organization-name">组织名称</Label>
            <Input
              id="organization-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Cable Demo Organization"
              required
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="organization-slug">组织标识</Label>
            <Input
              id="organization-slug"
              readOnly
              value={organization?.slug ?? ''}
              placeholder="cable-demo"
            />
            <p className="text-sm text-muted-foreground">
              组织 ID
              用于系统内部多租户隔离与数据归属，组织标识用于人可读展示与外部识别。
            </p>
          </div>

          <LogoUpload value={logo} onChange={setLogo} disabled={busy} />

          <div className="rounded-lg bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
            当前组织 ID:{' '}
            <span className="font-medium text-foreground">
              {organization?.id ?? '-'}
            </span>
          </div>

          {error ? (
            <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          {success ? (
            <div className="rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-600">
              {success}
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button disabled={busy} type="submit">
              {busy ? '保存中...' : '保存组织设置'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
